import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api';
import { toast } from 'sonner';
import { Mail, ArrowRight, Loader2, KeyRound, ChevronLeft } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await authApi.sendOtp(email.trim());
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true);
    try {
      const res = await authApi.verifyOtp(email.trim(), otp.trim());
      localStorage.setItem('outreach_token', res.token);
      navigate('/outreach');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" style={{ fontFamily: 'Manrope, Inter, sans-serif' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1A73E8] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#202124]">StartEase Outreach</h1>
          <p className="text-[#5F6368] mt-1 text-sm">Sign in with your @snigdha360.com email</p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendOTP} className="bg-white border border-[#E2E8F0] rounded-2xl p-8 space-y-5 shadow-sm" data-testid="otp-email-form">
            <div>
              <label className="text-sm text-[#5F6368] font-medium block mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#9AA0A6] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#F1F3F4] border border-transparent rounded-lg pl-10 pr-4 py-2.5 text-[#202124] text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20 transition-colors"
                  placeholder="you@snigdha360.com" autoFocus data-testid="login-email"
                />
              </div>
              <p className="text-xs text-[#9AA0A6] mt-1.5">We'll send a one-time code to your email</p>
            </div>
            <button
              type="submit" disabled={loading || !email.trim()}
              className="w-full bg-[#1A73E8] hover:bg-[#174EA6] disabled:opacity-50 text-white rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
              data-testid="send-otp-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>Send OTP</span><ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="bg-white border border-[#E2E8F0] rounded-2xl p-8 space-y-5 shadow-sm" data-testid="otp-verify-form">
            <div>
              <button type="button" onClick={() => { setStep('email'); setOtp(''); }}
                className="flex items-center gap-1 text-xs text-[#1A73E8] hover:text-[#174EA6] mb-3 font-medium" data-testid="back-to-email">
                <ChevronLeft className="w-3.5 h-3.5" /> Change email
              </button>
              <p className="text-sm text-[#5F6368] mb-4">
                Enter the 6-digit code sent to <span className="font-semibold text-[#202124]">{email}</span>
              </p>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-[#9AA0A6] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full bg-[#F1F3F4] border border-transparent rounded-lg pl-10 pr-4 py-2.5 text-[#202124] text-sm tracking-[0.3em] font-mono text-center focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20 transition-colors"
                  placeholder="000000" autoFocus maxLength={6} data-testid="login-otp"
                />
              </div>
            </div>
            <button
              type="submit" disabled={loading || otp.length < 6}
              className="w-full bg-[#1A73E8] hover:bg-[#174EA6] disabled:opacity-50 text-white rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
              data-testid="verify-otp-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>Verify & Sign In</span><ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={handleSendOTP} disabled={loading}
              className="w-full text-sm text-[#1A73E8] hover:text-[#174EA6] font-medium disabled:opacity-50" data-testid="resend-otp-btn">
              Resend OTP
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
